import type { SyncDownloadProgress, SyncPushProgress } from '@core/domain/sync/SyncPushResult.ts';
import { formatByteCount, syncProgressPercent } from '@src/modules/shell-ui/infrastructure/shellContentUi.ts';

export const CATALOG_SYNC_OVERLAY_ID = 'shell-catalog-sync-overlay';
export const SYNC_PUSH_OVERLAY_ID = 'shell-sync-push-overlay';

function syncPushOverlayCardHtml(p: {
  title: string;
  subtitle: string;
  progress: SyncPushProgress | null;
  spinner?: boolean;
}): string {
  const pct = p.progress ? syncProgressPercent(p.progress) : 0;
  const progressBlock = p.progress
    ? `<div class="shell-sync-upload-progress w-full min-w-[14rem]" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}" aria-label="Cargando registros">
        <div class="shell-sync-upload-progress__track" aria-hidden="true">
          <div class="shell-sync-upload-progress__fill" style="width: ${pct}%"></div>
        </div>
        <p class="mt-2 text-center text-sm font-medium">${pct}%</p>
      </div>`
    : p.spinner
      ? '<div class="shell-sync-overlay__spinner" aria-hidden="true"></div>'
      : '';
  return `<div class="shell-sync-overlay__card">
    ${progressBlock}
    <p class="m-0 text-sm font-medium">${p.title}</p>
    <p class="m-0 text-xs text-[color:var(--md-sys-color-on-surface-variant)]">${p.subtitle}</p>
  </div>`;
}

function mountOverlay(id: string, ariaLabel: string, cardHtml: string): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('div');
    el.id = id;
    el.className = 'shell-sync-overlay';
    el.setAttribute('role', 'alertdialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-busy', 'true');
    el.setAttribute('aria-live', 'polite');
    document.body.append(el);
  }
  el.setAttribute('aria-label', ariaLabel);
  el.innerHTML = cardHtml;
}

/** Catalog pull spinner (login / reconnect). */
export function showCatalogSyncOverlay(): void {
  if (typeof document === 'undefined' || document.getElementById(CATALOG_SYNC_OVERLAY_ID)) return;
  mountOverlay(
    CATALOG_SYNC_OVERLAY_ID,
    'Sincronizando catálogos',
    syncPushOverlayCardHtml({
      title: 'Sincronizando catálogos…',
      subtitle: 'Descargando datos actualizados del servidor',
      progress: null,
      spinner: true,
    }),
  );
}

/** Catalog pull progress (HTTP download). */
export function updateCatalogSyncPullOverlay(progress: SyncDownloadProgress): void {
  const el = document.getElementById(CATALOG_SYNC_OVERLAY_ID);
  if (!el) return;
  el.setAttribute('aria-label', 'Sincronizando catálogos');
  const mapped =
    progress.total != null && progress.total > 0
      ? ({ processed: progress.loaded, total: progress.total } satisfies SyncPushProgress)
      : null;
  el.innerHTML = syncPushOverlayCardHtml({
    title: 'Sincronizando catálogos…',
    subtitle:
      mapped != null
        ? 'Descargando catálogos del servidor'
        : `Descargando… ${formatByteCount(progress.loaded)}`,
    progress: mapped,
    spinner: mapped == null,
  });
}

/** Switch catalog overlay to offline asset warmup (post-sync). */
export function morphCatalogSyncOverlayToWarmup(): void {
  updateCatalogSyncWarmupOverlay({ processed: 0, total: 1 });
}

/** Updates offline warmup progress on the catalog overlay. */
export function updateCatalogSyncWarmupOverlay(progress: SyncPushProgress): void {
  const el = document.getElementById(CATALOG_SYNC_OVERLAY_ID);
  if (!el) return;
  el.setAttribute('aria-label', 'Preparando modo offline');
  el.innerHTML = syncPushOverlayCardHtml({
    title: 'Preparando modo offline…',
    subtitle:
      progress.total > 1
        ? `Mapas del catálogo: ${progress.processed} de ${progress.total}`
        : 'Descargando páginas, plantillas y mapas',
    progress: progress.total > 1 ? progress : null,
    spinner: progress.total <= 1,
  });
}

/** Switch catalog overlay to push progress (post-login pending records). */
export function morphCatalogSyncOverlayToPush(progress: SyncPushProgress): void {
  const el = document.getElementById(CATALOG_SYNC_OVERLAY_ID);
  if (!el) return;
  el.setAttribute('aria-label', 'Enviando registros');
  el.innerHTML = syncPushOverlayCardHtml({
    title: 'Enviando registros…',
    subtitle: 'Subiendo entregas y reposiciones al servidor',
    progress,
  });
}

export function updateCatalogSyncPushOverlay(progress: SyncPushProgress): void {
  const el = document.getElementById(CATALOG_SYNC_OVERLAY_ID);
  if (!el) return;
  el.innerHTML = syncPushOverlayCardHtml({
    title: 'Enviando registros…',
    subtitle: 'Subiendo entregas y reposiciones al servidor',
    progress,
  });
}

export function hideCatalogSyncOverlay(): void {
  document.getElementById(CATALOG_SYNC_OVERLAY_ID)?.remove();
}

/** Full-screen push overlay (manual sync page upload). */
export function showSyncPushOverlay(progress: SyncPushProgress): void {
  mountOverlay(
    SYNC_PUSH_OVERLAY_ID,
    'Enviando registros',
    syncPushOverlayCardHtml({
      title: 'Enviando registros…',
      subtitle: 'Subiendo entregas y reposiciones al servidor',
      progress,
    }),
  );
}

export function updateSyncPushOverlay(progress: SyncPushProgress): void {
  const el = document.getElementById(SYNC_PUSH_OVERLAY_ID);
  if (!el) return;
  el.setAttribute('aria-label', 'Enviando registros');
  el.innerHTML = syncPushOverlayCardHtml({
    title: 'Enviando registros…',
    subtitle: 'Subiendo entregas y reposiciones al servidor',
    progress,
  });
}

export function hideSyncPushOverlay(): void {
  document.getElementById(SYNC_PUSH_OVERLAY_ID)?.remove();
}
